import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ShieldCheck, Leaf, Users, Award, Eye, Mountain, Handshake } from "lucide-react";
import { KineticLine, Reveal, Overline, CountUp } from "@/components/Kinetic";
import Marquee from "@/components/Marquee";
import { IMAGES, MANIFESTO, MEGA_TRENDS } from "@/data/site";

const VALUE_CARDS = [
  { icon: ShieldCheck, title: "Integrity", desc: "We do what is right, always." },
  { icon: Award, title: "Reliability", desc: "Consistent supply, trusted delivery." },
  { icon: Leaf, title: "Sustainability", desc: "Responsible sourcing for a better future." },
  { icon: Users, title: "Partnership", desc: "Growing together, success shared." },
];

const CHAPTER_ICONS = [Mountain, Eye, Handshake, Award];

export default function About() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <main data-testid="about-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[70vh] items-end overflow-hidden" data-testid="about-hero">
        <motion.img
          src={IMAGES.field}
          alt="Green grain fields of India"
          style={{ y: imgY, scale: 1.1 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-forest/70" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-forest to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
          <Overline>Who We Are</Overline>
          <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-cream" data-testid="about-heading">
            <KineticLine delay={0.25} className="text-5xl md:text-6xl lg:text-7xl">
              A Global Platform for
            </KineticLine>
            <KineticLine delay={0.4} className="text-5xl md:text-6xl lg:text-7xl">
              Agricultural Trade<span className="text-gold">.</span>
            </KineticLine>
          </h1>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="about-intro">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Overline>Nishmayy Global</Overline>
            <motion.img
              src="/images/logo.png"
              alt="Nishmayy Global emblem"
              data-testid="about-brand-emblem"
              className="mt-12 w-52 md:w-64 lg:w-72"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
            />
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="font-serif text-2xl font-light leading-relaxed text-cream md:text-3xl lg:text-4xl">
                Nishmayy Global is an integrated agricultural commodities house and dry fruit
                processing manufacturer — connecting reliable producers in India with buyers worldwide.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-sage md:text-lg">
                From our processing unit in New Delhi, we combine deep domain expertise, robust supply
                networks, quality assurance and global logistics to deliver consistent value across the
                food value chain — from farmgate to global destinations.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="border-y border-forest-border bg-forest-surface py-28" data-testid="about-manifesto">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><Overline>Purpose-Driven · Partnership-Led · Performance-Delivered</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-4xl font-light text-cream md:text-5xl">The Manifesto</h2>
          </Reveal>
          <div className="mt-16 space-y-0 border-t border-forest-border">
            {MANIFESTO.map((m, i) => {
              const Icon = CHAPTER_ICONS[i];
              return (
                <Reveal key={m.no} delay={i * 0.08}>
                  <div className="group grid gap-6 border-b border-forest-border py-12 transition-colors duration-500 hover:bg-forest-light/50 md:grid-cols-12 md:items-center md:px-6" data-testid={`manifesto-${m.no}`}>
                    <div className="flex items-center gap-6 md:col-span-3">
                      <span className="font-serif text-6xl font-light text-gold/30 transition-colors duration-500 group-hover:text-gold md:text-7xl">
                        {m.no}
                      </span>
                      <Icon className="h-6 w-6 text-gold/60" strokeWidth={1.25} />
                    </div>
                    <div className="md:col-span-9">
                      <h3 className="font-serif text-2xl font-light text-cream md:text-3xl">{m.title}</h3>
                      <p className="mt-3 max-w-3xl text-base font-light leading-relaxed text-sage">{m.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="about-values">
        <Reveal><Overline>Our Values</Overline></Reveal>
        <div className="mt-12 grid gap-px border border-forest-border bg-forest-border sm:grid-cols-2 lg:grid-cols-4">
          {VALUE_CARDS.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1} className="bg-forest">
              <div className="group h-full px-8 py-12 transition-colors duration-500 hover:bg-forest-light" data-testid={`value-${v.title.toLowerCase()}`}>
                <v.icon className="h-8 w-8 text-gold" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-2xl font-light text-cream">{v.title}</h3>
                <p className="mt-2 text-sm font-light text-sage">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* MEGA TRENDS BENTO */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="about-trends">
        <Reveal><Overline>The Global Food System is Evolving</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
            Key mega trends shaping agricultural trade
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
          {MEGA_TRENDS.map((t, i) => (
            <Reveal
              key={t.title}
              delay={i * 0.08}
              className={i === 0 ? "lg:col-span-4" : i === 1 ? "lg:col-span-2" : "lg:col-span-2"}
            >
              <div className="group h-full border border-forest-border bg-forest-surface px-8 py-10 transition-colors duration-500 hover:border-gold/40" data-testid={`trend-${i}`}>
                <p className="font-serif text-4xl font-light text-gold/40 transition-colors duration-500 group-hover:text-gold">
                  0{i + 1}
                </p>
                <h3 className="mt-5 text-lg font-semibold tracking-wide text-cream">{t.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-sage">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FOOTPRINT */}
      <section className="relative overflow-hidden border-t border-forest-border" data-testid="about-footprint">
        <img src={IMAGES.port} alt="Global logistics at sunset" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest/85" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 py-28 lg:px-10">
          <Reveal><Overline>Global Reach · Local Expertise · Trusted Partnerships</Overline></Reveal>
          <div className="mt-12 grid gap-12 md:grid-cols-3">
            {[
              { v: 93, s: "+", l: "Countries", d: "Strong presence across key importing regions worldwide." },
              { v: 4000, s: "+", l: "Farm Partners", d: "Sourcing from diverse agro-climatic regions across India." },
              { v: 100, s: "%", l: "Quality First", d: "International standards. Zero compromise." },
            ].map((s, i) => (
              <Reveal key={s.l} delay={i * 0.12}>
                <p className="font-serif text-6xl font-light text-gold">
                  <CountUp to={s.v} suffix={s.s} />
                </p>
                <p className="mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-cream">{s.l}</p>
                <p className="mt-2 text-sm font-light text-sage">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
